import React, { useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiDownload, FiX } from "react-icons/fi";
import { GenePlotChart, downloadSvgAsPng } from "../ReusableComponent/GeneDetailPanel";
import { survivalGroupDisplay } from "../ReusableComponent/survivalGroupDisplay";
import KaplanMeierChart from "./KaplanMeierChart";
import { surgeryRadiationLabel } from "./surgeryRadiationLabel";

const number = (value, digits = 4) => {
  if (value == null || value === "" || !Number.isFinite(Number(value))) return "—";
  const numeric = Number(value);
  return numeric !== 0 && Math.abs(numeric) < 0.0001 ? numeric.toExponential(3) : numeric.toFixed(digits);
};
const count = (value) => value == null ? "—" : Number(value).toLocaleString();

export default function ProjectionGeneDetailPanel({ panel, projection, onClose, onRetry }) {
  const { row, resultType, loading, data, error } = panel;
  const [open, setOpen] = useState(false);
  const [showData, setShowData] = useState(false);
  const drawerRef = useRef(null);
  const chartRef = useRef(null);
  const plot = data?.plot;
  const parameters = data?.parameters || {};
  const dataType = parameters.dataLevel || projection.molecular_type;
  const metadata = data?.metadata || {};
  const isCox = resultType === "cox";
  const ready = !loading && !error && (isCox
    ? Array.isArray(plot?.curves) && plot.curves.some((curve) => Array.isArray(curve.points) && curve.points.length > 0)
    : Array.isArray(plot?.points) && plot.points.length > 0);
  const metrics = isCox
    ? [["Hazard ratio", number(row.hazardRatio)], ["Cox p-value", number(row.pValue)], ["Patients", count(row.patientCount)]]
    : [["Effect estimate", number(row.estimate)], ["p-value", number(row.pValue)], ["Matched strata", count(row.matchedStrata)]];

  useEffect(() => {
    const previous = document.activeElement;
    const frame = requestAnimationFrame(() => setOpen(true));
    drawerRef.current?.querySelector("button")?.focus();
    return () => { cancelAnimationFrame(frame); previous?.focus?.(); };
  }, []);

  const onKeyDown = (event) => {
    if (event.key === "Escape") { event.stopPropagation(); onClose(); }
    if (event.key !== "Tab") return;
    const controls = [...drawerRef.current.querySelectorAll('button:not([disabled]), [tabindex="0"]')];
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };

  return <>
    <div className="bmt-drawer-backdrop" onClick={onClose} />
    <aside ref={drawerRef} className={`pgd-drawer projection-gene-drawer${open ? " is-open" : ""}`}
      role="dialog" aria-modal="true" aria-label={`${row.gene} gene details`} onKeyDown={onKeyDown}>
      <div className="pgd-scroll">
        <div className="pgd-top">
          <button type="button" className="pgd-back" onClick={onClose}><FiArrowLeft /> Gene details</button>
          <button type="button" className="pgd-close" aria-label="Close gene details" onClick={onClose}><FiX /></button>
        </div>
        <h2>{row.gene}</h2>
        <div className="pgd-tags"><span className="pgd-badge">{isCox ? "Cox / survival" : resultType === "treatment" ? "Treatment projection" : "Prognostic projection"}</span></div>
        <p className="projection-gene-context">
          {[parameters.histological_subtype || projection.histological_subtype,
            parameters.dataLevel || projection.molecular_type,
            parameters.inputVariables || projection.input_variables].filter(Boolean).join(" · ")}
          {resultType === "treatment" && <><br /><strong>{row.treatment}</strong><br />{surgeryRadiationLabel(row.SurgRad)}</>}
        </p>
        <div className="pgd-metrics">
          {metrics.map(([label, value]) => (
            <div className="pgd-metric" key={label}><span>{label}</span><strong>{value}</strong></div>
          ))}
        </div>
        <section className="pgd-plot-card" aria-label={isCox ? "Kaplan–Meier overall survival" : "Expression vs Average Survival"}>
          <div className="pgd-plot-title">{isCox ? "Kaplan–Meier overall survival" : plot?.title || "Expression vs Average Survival"}</div>
          {ready ? (isCox ? <KaplanMeierChart dataType={dataType} plot={plot} containerRef={chartRef} />
            : <GenePlotChart plot={plot} containerRef={chartRef} scatterLabel="Matched stage groups" />)
            : <div className="projection-plot-message" role={error ? "alert" : "status"}>
              {loading ? "Loading plot…" : error || "No plot data available for this gene."}
              {error && <button type="button" className="bmt-btn-reset mt-3" onClick={onRetry}>Retry</button>}
            </div>}
        </section>
        <dl className="projection-plot-summary">
          {isCox ? <>
            <div><dt>Log-rank p-value</dt><dd>{number(plot?.logRankPValue)}</dd></div>
            <div><dt>95% CI (Cox HR)</dt><dd>{number(row.ciLower)} – {number(row.ciUpper)}</dd></div>
            <div><dt>Events (Cox model)</dt><dd>{count(row.eventCount)}</dd></div>
            <div><dt>Patients excluded (plot)</dt><dd>{count(metadata.excludedPatientCount)}</dd></div>
            <div><dt>Expression cutpoint</dt><dd>{number(plot?.cutpoint)}</dd></div>
          </> : <>
          <div><dt>Correlation</dt><dd>{number(plot?.regression?.r)}</dd></div>
          <div><dt>Uploaded patients represented</dt><dd>{count(metadata.userN)}</dd></div>
          <div><dt>SEER patients represented</dt><dd>{count(metadata.seerN)}</dd></div>
          {plot?.regression?.equation && <div><dt>Regression</dt><dd>{plot.regression.equation}</dd></div>}
          </>}
        </dl>
        <p className="projection-gene-note">{isCox
          ? "The Cox model p-value and the plot’s log-rank p-value are different tests. Survival groups are determined automatically."
          : "Each point is a matched stage group, not an individual patient."}</p>
        <div className="pgd-actions">
          <button type="button" className="pgd-view-data" disabled={!ready} aria-expanded={showData && ready}
            onClick={() => setShowData((value) => !value)}>{showData ? "Hide data" : "View data"}</button>
          <button type="button" className="pgd-download" disabled={!ready}
            onClick={() => downloadSvgAsPng(chartRef.current?.querySelector("svg"), `${row.gene}_${resultType}_plot.png`)}>
            <FiDownload /> Download plot</button>
        </div>
        {showData && ready && <div className="projection-plot-data" tabIndex={0} aria-label="Plot data">
          {isCox ? <table><thead><tr><th>Group</th><th>{plot.x_axis?.label || "Time (Days)"}</th>
            <th>{plot.y_axis?.label || "Survival Probability"}</th><th>At risk</th><th>Events</th><th>Censored</th></tr></thead>
            <tbody>{plot.curves.flatMap((curve, curveIndex) => (curve.points || []).map((point, index) => <tr key={`${curveIndex}-${index}`}>
              <td>{survivalGroupDisplay(dataType, curve.group).label}</td><td>{point.time}</td><td>{number(point.survival)}</td>
              <td>{count(point.at_risk)}</td><td>{count(point.events)}</td><td>{count(point.censored)}</td>
            </tr>))}</tbody></table>
          : <table><thead><tr><th>Stage group</th><th>{plot.x_axis?.label || "Expression"}</th>
            <th>{plot.y_axis?.label || "Average Survival"}</th><th>SEER patients</th><th>Uploaded patients</th></tr></thead>
            <tbody>{plot.points.map((point, index) => <tr key={index}>
              <td>{Object.entries(point.tnm || {}).map(([key, value]) => `${key.toUpperCase()}: ${value}`).join(" · ") || "—"}</td>
              <td>{number(point[plot.x_axis?.key || "expression"])}</td>
              <td>{number(point[plot.y_axis?.key || "avg_survival"])}</td>
              <td>{count(point.sample_count?.seer)}</td><td>{count(point.sample_count?.user)}</td>
            </tr>)}</tbody>
          </table>}
        </div>}
      </div>
    </aside>
  </>;
}
